import { call, put, select, takeEvery } from 'redux-saga/effects';
import {
  CASE_DUPLICATE_FILE_BEGIN,
  CASE_DUPLICATE_FILE_SUCCESS,
  CASE_DUPLICATE_FILE_FAILURE,
  CASE_DUPLICATE_FILE_DISMISS_FEEDBACK,
  CASE_FETCH_DOCUMENTS_BEGIN,
} from './constants';
import {
  createPromiseAction,
  rejectPromiseAction,
  resolvePromiseAction,
} from '@adobe/redux-saga-promise';
import api from 'common/api';
import { withCurrentCaseId, selectCurrentFolder, selectDocuments } from 'common/selectors';
import addSortNumberToDocuments from './addSortNumberToDocuments';

export const duplicateFile = createPromiseAction(CASE_DUPLICATE_FILE_BEGIN);

export function dismissDuplicateFileFeedback() {
  return {
    type: CASE_DUPLICATE_FILE_DISMISS_FEEDBACK,
  };
}

const extractDuplicatedDocument = (response) => {
  if (!response || response.error) {
    return null;
  }
  if (response.file && typeof response.file === 'object') {
    return response.file;
  }
  if (response.result && typeof response.result === 'object') {
    return response.result;
  }
  if (Array.isArray(response.results) && response.results.length > 0) {
    return response.results[0];
  }
  if (Array.isArray(response.files) && response.files.length > 0) {
    return response.files[0];
  }
  return typeof response === 'object' ? response : null;
};

const splitFileName = (name = '') => {
  const safeName = String(name || '').trim();
  if (!safeName) {
    return {
      baseName: '',
      extension: '',
    };
  }
  const lastDotIndex = safeName.lastIndexOf('.');
  if (lastDotIndex <= 0) {
    return {
      baseName: safeName,
      extension: '',
    };
  }
  return {
    baseName: safeName.slice(0, lastDotIndex),
    extension: safeName.slice(lastDotIndex),
  };
};

const getDocumentName = (document = {}) => {
  const candidates = [
    document.name,
    document.originalName,
    document.fileName,
    document.filename,
    document.file?.name,
  ];
  const firstValid = candidates.find((value) => typeof value === 'string' && value.trim());
  return firstValid ? firstValid.trim() : '';
};

const getCopyRoot = (baseName = '') => {
  const copyMatch = baseName.match(/^(.*)-copy(?:\((\d+)\))?$/i);
  return copyMatch && copyMatch[1] ? copyMatch[1] : baseName;
};

const escapeRegExp = (value = '') => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getCopyIndex = (baseName = '', copyRoot = '') => {
  if (!baseName || !copyRoot) {
    return 0;
  }
  const plainCopyName = `${copyRoot}-copy`;
  if (baseName.toLowerCase() === plainCopyName.toLowerCase()) {
    return 1;
  }
  const match = baseName.match(new RegExp(`^${escapeRegExp(copyRoot)}-copy\\((\\d+)\\)$`, 'i'));
  if (!match || !match[1]) {
    return 0;
  }
  const parsedIndex = Number.parseInt(match[1], 10);
  return Number.isFinite(parsedIndex) && parsedIndex > 0 ? parsedIndex : 0;
};

const documentMatchesId = (document = {}, fileId) => {
  const targetId = String(fileId || '');
  if (!targetId) {
    return false;
  }
  const docIds = [document.id, document.folderFileId, document.fileId, document.compositeKey];
  return docIds.some((value) => value != null && String(value) === targetId);
};

const findDocumentById = (documents = [], fileId) =>
  documents.find((document) => documentMatchesId(document, fileId)) || null;

const getDocumentIdentifier = (document = {}) =>
  document.folderFileId || document.id || document.fileId || document.compositeKey || null;

export const getNextDuplicateName = ({ documents = [], sourceDocument = null, fallbackName = '' }) => {
  const sourceName = getDocumentName(sourceDocument) || String(fallbackName || '').trim();
  if (!sourceName) {
    return '';
  }

  const { baseName: sourceBaseName, extension: sourceExtension } = splitFileName(sourceName);
  if (!sourceBaseName) {
    return '';
  }

  const copyRoot = getCopyRoot(sourceBaseName);
  if (!copyRoot) {
    return '';
  }

  const normalizedSourceExtension = sourceExtension.toLowerCase();
  const highestExistingCopyIndex = documents.reduce((highestIndex, document) => {
    const currentName = getDocumentName(document);
    if (!currentName) {
      return highestIndex;
    }
    const { baseName, extension } = splitFileName(currentName);
    if (extension.toLowerCase() !== normalizedSourceExtension) {
      return highestIndex;
    }
    const copyIndex = getCopyIndex(baseName, copyRoot);
    return copyIndex > highestIndex ? copyIndex : highestIndex;
  }, 0);

  const nextCopyIndex = highestExistingCopyIndex + 1;
  const suffix = nextCopyIndex > 1 ? `-copy(${nextCopyIndex})` : '-copy';
  return `${copyRoot}${suffix}${sourceExtension}`;
};

export function* doDuplicateFile(action) {
  const {
    payload: { caseId, folderId, fileId, body = {} },
  } = action;
  const currentFolder = yield select(selectCurrentFolder);
  const currentDocuments = yield select(selectDocuments);

  if (!folderId || !fileId) {
    const error = new Error('Missing folderId or fileId for duplicate file request.');
    yield put({
      type: CASE_DUPLICATE_FILE_FAILURE,
      feedback: {
        message: 'feedback.duplicateFileFailure',
        error,
      },
    });
    return yield call(rejectPromiseAction, action, { error });
  }

  const response = yield call(
    api.post,
    `/cases/${caseId}/folders/${folderId}/files/${fileId}/duplicate`,
    body,
  );

  if (response && response.error) {
    yield put({
      type: CASE_DUPLICATE_FILE_FAILURE,
      feedback: {
        message: 'feedback.duplicateFileFailure',
        error: response.error,
        retryAction: action,
      },
    });
    return yield call(rejectPromiseAction, action, response.error);
  }

  let duplicatedDocument = extractDuplicatedDocument(response);

  const shouldInsert =
    !!duplicatedDocument &&
    currentFolder &&
    String(currentFolder.id || '') === String(folderId || '');
  const requestedDuplicateName =
    body && typeof body.name === 'string' ? body.name.trim() : '';

  if (shouldInsert && duplicatedDocument) {
    const duplicatedDocumentName = getDocumentName(duplicatedDocument);
    const duplicatedDocumentId = getDocumentIdentifier(duplicatedDocument);
    const sourceDocument = findDocumentById(currentDocuments, fileId);
    const desiredName = requestedDuplicateName
      ? requestedDuplicateName
      : getNextDuplicateName({
          documents: currentDocuments,
          sourceDocument,
          fallbackName: duplicatedDocumentName,
        });

    if (desiredName && duplicatedDocumentId && desiredName !== duplicatedDocumentName) {
      const renamePayload = currentFolder?.etag
        ? { name: desiredName, etag: currentFolder.etag }
        : { name: desiredName };
      const renameResponse = yield call(
        api.put,
        `/cases/${caseId}/folders/${folderId}/files/${duplicatedDocumentId}`,
        renamePayload,
      );
      if (!renameResponse || !renameResponse.error) {
        duplicatedDocument = extractDuplicatedDocument(renameResponse) || {
          ...duplicatedDocument,
          name: desiredName,
        };
      }
    }
  }

  const shouldRefreshFolder =
    !duplicatedDocument &&
    currentFolder &&
    String(currentFolder.id || '') === String(folderId || '');

  if (shouldRefreshFolder) {
    yield put({ type: CASE_FETCH_DOCUMENTS_BEGIN });
  }

  yield put({
    type: CASE_DUPLICATE_FILE_SUCCESS,
    data: {
      sourceFileId: fileId,
      document: duplicatedDocument,
      shouldInsert,
      zeroBasedIndex: currentFolder?.zeroBasedIndex,
    },
    feedback: {
      message: 'feedback.duplicateFileSuccess',
    },
  });

  return yield call(resolvePromiseAction, action, response);
}

export function* watchDuplicateFile() {
  yield takeEvery(duplicateFile, withCurrentCaseId(doDuplicateFile));
}

const findDocumentInsertIndex = (documents, sourceFileId) => {
  if (!Array.isArray(documents) || !documents.length) {
    return 0;
  }
  const sourceId = String(sourceFileId || '');
  const sourceIndex = documents.findIndex(
    (doc) =>
      String(doc.id || '') === sourceId ||
      String(doc.folderFileId || '') === sourceId ||
      String(doc.compositeKey || '') === sourceId,
  );
  return sourceIndex > -1 ? sourceIndex + 1 : documents.length;
};

export function reducer(state, action) {
  switch (action.type) {
    case CASE_DUPLICATE_FILE_BEGIN + '.TRIGGER':
      return {
        ...state,
        duplicateFilePending: true,
        duplicateFileFeedback: null,
      };

    case CASE_DUPLICATE_FILE_SUCCESS: {
      const canInsert = action.data.shouldInsert && action.data.document;
      const insertIndex = findDocumentInsertIndex(state.documents, action.data.sourceFileId);
      const updatedDocuments = canInsert
        ? [
            ...state.documents.slice(0, insertIndex),
            action.data.document,
            ...state.documents.slice(insertIndex),
          ]
        : state.documents;
      return {
        ...state,
        duplicateFilePending: false,
        duplicateFileFeedback: action.feedback,
        ...(canInsert && {
          documents: addSortNumberToDocuments(updatedDocuments, action.data.zeroBasedIndex),
          totalDocuments:
            typeof state.totalDocuments === 'number' ? state.totalDocuments + 1 : state.totalDocuments,
        }),
      };
    }

    case CASE_DUPLICATE_FILE_FAILURE:
      return {
        ...state,
        duplicateFilePending: false,
        duplicateFileFeedback: action.feedback,
      };

    case CASE_DUPLICATE_FILE_DISMISS_FEEDBACK:
      return {
        ...state,
        duplicateFileFeedback: null,
      };

    default:
      return state;
  }
}
